import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { Html, OrbitControls } from '@react-three/drei';
import * as THREE from 'three';
import { BODY_REGION_DEFINITIONS } from '../../data/bodyMapRegions';
import type { BodyIssueRecord, BodyRegionKey, BodySystemTag } from '../../types/bodyMapIssue';

interface Props {
  selectedRegionKey: BodyRegionKey | null;
  onSelectRegion: (regionKey: BodyRegionKey | null) => void;
  issues: BodyIssueRecord[];
}

const SYSTEM_OVERLAYS: Array<{ id: 'all' | BodySystemTag; label: string }> = [
  { id: 'all', label: 'All systems' },
  { id: 'musculoskeletal', label: 'Skeletal / muscular' },
  { id: 'neurological', label: 'Nervous' },
  { id: 'endocrine', label: 'Endocrine' },
  { id: 'gastrointestinal', label: 'GI' },
  { id: 'reproductive', label: 'Reproductive' },
  { id: 'respiratory', label: 'Respiratory' },
  { id: 'cardiometabolic', label: 'Cardiometabolic' },
  { id: 'dermatological', label: 'Skin' },
  { id: 'general', label: 'General' },
];

const CAMERA_PRESETS: Record<'full' | 'upper' | 'core' | 'lower', { position: [number, number, number]; target: [number, number, number] }> = {
  full: { position: [0, 1.25, 5.2], target: [0, 1.05, 0] },
  upper: { position: [0, 2.2, 3.55], target: [0, 1.82, 0] },
  core: { position: [0, 1.3, 2.95], target: [0, 1.05, 0] },
  lower: { position: [0, 0.3, 3.1], target: [0, -0.15, 0] },
};

function buildRegionStats(issues: BodyIssueRecord[]) {
  return issues.reduce<Record<string, { total: number; active: number; escalated: number }>>((accumulator, issue) => {
    const current = accumulator[issue.regionKey] || { total: 0, active: 0, escalated: 0 };
    current.total += 1;
    if (issue.status !== 'resolved') current.active += 1;
    if (issue.status === 'escalated') current.escalated += 1;
    accumulator[issue.regionKey] = current;
    return accumulator;
  }, {});
}

type MeshKind = 'sphere' | 'capsule' | 'box' | 'foot' | 'hand';

interface MeshConfig {
  position: [number, number, number];
  rotation?: [number, number, number];
  scale: [number, number, number];
  kind: MeshKind;
}

const REGION_MESHES: Record<BodyRegionKey, MeshConfig> = {
  head: { position: [0, 2.74, 0], scale: [0.54, 0.8, 0.56], kind: 'sphere' },
  neck: { position: [0, 2.05, 0], scale: [0.18, 0.32, 0.18], kind: 'capsule' },
  chest: { position: [0, 1.48, 0.02], scale: [0.78, 0.86, 0.34], kind: 'sphere' },
  abdomen: { position: [0, 0.62, 0.02], scale: [0.62, 0.82, 0.28], kind: 'sphere' },
  pelvis: { position: [0, -0.14, 0], scale: [0.74, 0.26, 0.28], kind: 'capsule' },
  upper_back: { position: [0, 1.5, -0.14], scale: [0.76, 0.82, 0.16], kind: 'sphere' },
  lower_back: { position: [0, 0.62, -0.13], scale: [0.62, 0.76, 0.14], kind: 'sphere' },
  left_clavicle: { position: [-0.3, 1.9, 0.16], rotation: [0, 0, -0.22], scale: [0.22, 0.07, 0.07], kind: 'capsule' },
  right_clavicle: { position: [0.3, 1.9, 0.16], rotation: [0, 0, 0.22], scale: [0.22, 0.07, 0.07], kind: 'capsule' },
  left_shoulder: { position: [-0.98, 1.58, 0.02], scale: [0.22, 0.26, 0.22], kind: 'sphere' },
  right_shoulder: { position: [0.98, 1.58, 0.02], scale: [0.22, 0.26, 0.22], kind: 'sphere' },
  left_shoulder_blade: { position: [-0.42, 1.5, -0.16], rotation: [0.14, 0, -0.12], scale: [0.22, 0.3, 0.06], kind: 'box' },
  right_shoulder_blade: { position: [0.42, 1.5, -0.16], rotation: [0.14, 0, 0.12], scale: [0.22, 0.3, 0.06], kind: 'box' },
  left_chest_wall: { position: [-0.24, 1.42, 0.15], scale: [0.26, 0.34, 0.08], kind: 'sphere' },
  right_chest_wall: { position: [0.24, 1.42, 0.15], scale: [0.26, 0.34, 0.08], kind: 'sphere' },
  sternum: { position: [0, 1.4, 0.18], scale: [0.07, 0.4, 0.05], kind: 'capsule' },
  left_upper_arm: { position: [-1.22, 0.96, 0], rotation: [0, 0, -0.18], scale: [0.17, 0.88, 0.16], kind: 'capsule' },
  right_upper_arm: { position: [1.22, 0.96, 0], rotation: [0, 0, 0.18], scale: [0.17, 0.88, 0.16], kind: 'capsule' },
  left_biceps: { position: [-1.12, 1.08, 0.14], rotation: [0.08, 0, -0.18], scale: [0.13, 0.36, 0.08], kind: 'capsule' },
  right_biceps: { position: [1.12, 1.08, 0.14], rotation: [0.08, 0, 0.18], scale: [0.13, 0.36, 0.08], kind: 'capsule' },
  left_triceps: { position: [-1.18, 0.9, -0.12], rotation: [-0.08, 0, -0.18], scale: [0.12, 0.34, 0.07], kind: 'capsule' },
  right_triceps: { position: [1.18, 0.9, -0.12], rotation: [-0.08, 0, 0.18], scale: [0.12, 0.34, 0.07], kind: 'capsule' },
  left_elbow: { position: [-1.34, 0.16, 0], scale: [0.13, 0.13, 0.13], kind: 'sphere' },
  right_elbow: { position: [1.34, 0.16, 0], scale: [0.13, 0.13, 0.13], kind: 'sphere' },
  left_forearm: { position: [-1.46, -0.58, 0.04], rotation: [0, 0, 0.12], scale: [0.12, 0.8, 0.11], kind: 'capsule' },
  right_forearm: { position: [1.46, -0.58, 0.04], rotation: [0, 0, -0.12], scale: [0.12, 0.8, 0.11], kind: 'capsule' },
  left_wrist: { position: [-1.54, -1.26, 0.04], scale: [0.09, 0.09, 0.09], kind: 'sphere' },
  right_wrist: { position: [1.54, -1.26, 0.04], scale: [0.09, 0.09, 0.09], kind: 'sphere' },
  left_hand: { position: [-1.58, -1.55, 0.04], rotation: [0, 0, -0.16], scale: [0.18, 0.2, 0.1], kind: 'hand' },
  right_hand: { position: [1.58, -1.55, 0.04], rotation: [0, 0, 0.16], scale: [0.18, 0.2, 0.1], kind: 'hand' },
  left_palm: { position: [-1.57, -1.54, 0.08], rotation: [0, 0, -0.16], scale: [0.11, 0.13, 0.06], kind: 'hand' },
  right_palm: { position: [1.57, -1.54, 0.08], rotation: [0, 0, 0.16], scale: [0.11, 0.13, 0.06], kind: 'hand' },
  left_fingers: { position: [-1.65, -1.62, 0.14], rotation: [0, 0, -0.22], scale: [0.1, 0.08, 0.04], kind: 'hand' },
  right_fingers: { position: [1.65, -1.62, 0.14], rotation: [0, 0, 0.22], scale: [0.1, 0.08, 0.04], kind: 'hand' },
  left_hip: { position: [-0.34, -0.02, 0], scale: [0.18, 0.22, 0.18], kind: 'sphere' },
  right_hip: { position: [0.34, -0.02, 0], scale: [0.18, 0.22, 0.18], kind: 'sphere' },
  left_thigh: { position: [-0.32, -0.98, 0], rotation: [0, 0, 0.02], scale: [0.2, 1, 0.18], kind: 'capsule' },
  right_thigh: { position: [0.32, -0.98, 0], rotation: [0, 0, -0.02], scale: [0.2, 1, 0.18], kind: 'capsule' },
  left_knee: { position: [-0.32, -1.86, 0], scale: [0.14, 0.14, 0.14], kind: 'sphere' },
  right_knee: { position: [0.32, -1.86, 0], scale: [0.14, 0.14, 0.14], kind: 'sphere' },
  left_lower_leg: { position: [-0.32, -2.68, 0], scale: [0.16, 0.96, 0.15], kind: 'capsule' },
  right_lower_leg: { position: [0.32, -2.68, 0], scale: [0.16, 0.96, 0.15], kind: 'capsule' },
  left_shin: { position: [-0.27, -2.66, 0.1], scale: [0.08, 0.82, 0.04], kind: 'box' },
  right_shin: { position: [0.27, -2.66, 0.1], scale: [0.08, 0.82, 0.04], kind: 'box' },
  left_calf: { position: [-0.35, -2.65, -0.08], scale: [0.14, 0.8, 0.08], kind: 'box' },
  right_calf: { position: [0.35, -2.65, -0.08], scale: [0.14, 0.8, 0.08], kind: 'box' },
  left_ankle: { position: [-0.32, -3.5, 0], scale: [0.1, 0.1, 0.1], kind: 'sphere' },
  right_ankle: { position: [0.32, -3.5, 0], scale: [0.1, 0.1, 0.1], kind: 'sphere' },
  left_foot: { position: [-0.38, -3.84, 0.16], rotation: [0.14, 0, 0.08], scale: [0.34, 0.11, 0.16], kind: 'foot' },
  right_foot: { position: [0.38, -3.84, 0.16], rotation: [0.14, 0, -0.08], scale: [0.34, 0.11, 0.16], kind: 'foot' },
  left_heel: { position: [-0.48, -3.83, 0.02], rotation: [0.1, 0, 0.08], scale: [0.1, 0.09, 0.12], kind: 'foot' },
  right_heel: { position: [0.48, -3.83, 0.02], rotation: [0.1, 0, -0.08], scale: [0.1, 0.09, 0.12], kind: 'foot' },
  left_arch: { position: [-0.36, -3.83, 0.1], rotation: [0.12, 0, 0.08], scale: [0.12, 0.08, 0.09], kind: 'foot' },
  right_arch: { position: [0.36, -3.83, 0.1], rotation: [0.12, 0, -0.08], scale: [0.12, 0.08, 0.09], kind: 'foot' },
  left_forefoot: { position: [-0.25, -3.83, 0.21], rotation: [0.16, 0, 0.08], scale: [0.14, 0.07, 0.11], kind: 'foot' },
  right_forefoot: { position: [0.25, -3.83, 0.21], rotation: [0.16, 0, -0.08], scale: [0.14, 0.07, 0.11], kind: 'foot' },
  left_toes: { position: [-0.14, -3.84, 0.28], rotation: [0.2, 0, 0.06], scale: [0.08, 0.05, 0.08], kind: 'foot' },
  right_toes: { position: [0.14, -3.84, 0.28], rotation: [0.2, 0, -0.06], scale: [0.08, 0.05, 0.08], kind: 'foot' },
};

function meshColor(selected: boolean, stats?: { total: number; active: number; escalated: number }) {
  if (selected) return '#1A1A18';
  if (!stats) return '#ece8df';
  if (stats.escalated > 0) return '#d25f5f';
  if (stats.active > 1) return '#d4a08a';
  if (stats.active === 1) return '#d7c3a6';
  return '#b8c2ad';
}

function RegionMesh({
  regionKey,
  selected,
  stats,
  onSelect,
  onHover,
}: {
  regionKey: BodyRegionKey;
  selected: boolean;
  stats?: { total: number; active: number; escalated: number };
  onSelect: (regionKey: BodyRegionKey | null) => void;
  onHover: (regionKey: BodyRegionKey | null) => void;
}) {
  const config = REGION_MESHES[regionKey];
  if (!config) return null;
  const color = meshColor(selected, stats);
  const opacity = stats ? 0.96 : 0.16;
  const baseMaterial = (
    <meshStandardMaterial
      color={color}
      transparent
      opacity={opacity}
      metalness={0.08}
      roughness={0.72}
      emissive={selected ? new THREE.Color('#4F5D18') : new THREE.Color('#000000')}
      emissiveIntensity={selected ? 0.25 : 0}
    />
  );
  const commonProps = {
    position: config.position,
    rotation: config.rotation || [0, 0, 0] as [number, number, number],
    onClick: () => onSelect(regionKey),
    onPointerOver: () => onHover(regionKey),
    onPointerOut: () => onHover(null),
    castShadow: true,
    receiveShadow: true,
  };

  if (config.kind === 'sphere') {
    return (
      <mesh {...commonProps} scale={config.scale}>
        <sphereGeometry args={[0.5, 32, 32]} />
        {baseMaterial}
      </mesh>
    );
  }

  if (config.kind === 'box') {
    return (
      <mesh {...commonProps} scale={config.scale}>
        <boxGeometry args={[1, 1, 1]} />
        {baseMaterial}
      </mesh>
    );
  }

  if (config.kind === 'capsule') {
    return (
      <mesh {...commonProps} scale={config.scale}>
        <capsuleGeometry args={[0.5, 1.2, 8, 16]} />
        {baseMaterial}
      </mesh>
    );
  }

  if (config.kind === 'hand') {
    return (
      <group {...commonProps}>
        <mesh scale={config.scale}>
          <sphereGeometry args={[0.5, 18, 18]} />
          {baseMaterial}
        </mesh>
      </group>
    );
  }

  return (
    <mesh {...commonProps} scale={config.scale}>
      <capsuleGeometry args={[0.5, 0.7, 8, 16]} />
      {baseMaterial}
    </mesh>
  );
}

function BaseBody() {
  const material = <meshStandardMaterial color="#f4f1ea" metalness={0.02} roughness={0.86} transparent opacity={0.58} />;

  return (
    <group>
      <mesh position={[0, 2.74, 0]} scale={[0.56, 0.8, 0.56]} castShadow receiveShadow>
        <sphereGeometry args={[0.5, 32, 32]} />
        {material}
      </mesh>
      <mesh position={[0, 2.05, 0]} scale={[0.18, 0.32, 0.18]} castShadow receiveShadow>
        <capsuleGeometry args={[0.5, 0.8, 8, 16]} />
        {material}
      </mesh>
      <mesh position={[0, 1.48, 0.04]} scale={[0.8, 0.9, 0.34]} castShadow receiveShadow>
        <sphereGeometry args={[0.5, 32, 32]} />
        {material}
      </mesh>
      <mesh position={[0, 0.62, 0.02]} scale={[0.64, 0.82, 0.28]} castShadow receiveShadow>
        <sphereGeometry args={[0.5, 32, 32]} />
        {material}
      </mesh>
      <mesh position={[0, -0.14, 0]} scale={[0.74, 0.24, 0.28]} castShadow receiveShadow>
        <capsuleGeometry args={[0.5, 0.7, 8, 16]} />
        {material}
      </mesh>
      <mesh position={[-1.22, 0.96, 0]} rotation={[0, 0, -0.18]} scale={[0.16, 0.9, 0.15]} castShadow receiveShadow>
        <capsuleGeometry args={[0.5, 1.4, 8, 16]} />
        {material}
      </mesh>
      <mesh position={[1.22, 0.96, 0]} rotation={[0, 0, 0.18]} scale={[0.16, 0.9, 0.15]} castShadow receiveShadow>
        <capsuleGeometry args={[0.5, 1.4, 8, 16]} />
        {material}
      </mesh>
      <mesh position={[-1.46, -0.58, 0.04]} rotation={[0, 0, 0.12]} scale={[0.12, 0.82, 0.11]} castShadow receiveShadow>
        <capsuleGeometry args={[0.5, 1.2, 8, 16]} />
        {material}
      </mesh>
      <mesh position={[1.46, -0.58, 0.04]} rotation={[0, 0, -0.12]} scale={[0.12, 0.82, 0.11]} castShadow receiveShadow>
        <capsuleGeometry args={[0.5, 1.2, 8, 16]} />
        {material}
      </mesh>
      <mesh position={[-0.32, -0.98, 0]} scale={[0.2, 1, 0.18]} castShadow receiveShadow>
        <capsuleGeometry args={[0.5, 1.8, 8, 16]} />
        {material}
      </mesh>
      <mesh position={[0.32, -0.98, 0]} scale={[0.2, 1, 0.18]} castShadow receiveShadow>
        <capsuleGeometry args={[0.5, 1.8, 8, 16]} />
        {material}
      </mesh>
      <mesh position={[-0.32, -2.68, 0]} scale={[0.16, 0.96, 0.15]} castShadow receiveShadow>
        <capsuleGeometry args={[0.5, 1.6, 8, 16]} />
        {material}
      </mesh>
      <mesh position={[0.32, -2.68, 0]} scale={[0.16, 0.96, 0.15]} castShadow receiveShadow>
        <capsuleGeometry args={[0.5, 1.6, 8, 16]} />
        {material}
      </mesh>
      <mesh position={[-0.38, -3.84, 0.16]} rotation={[0.14, 0, 0.08]} scale={[0.34, 0.11, 0.16]} castShadow receiveShadow>
        <capsuleGeometry args={[0.5, 0.8, 8, 16]} />
        {material}
      </mesh>
      <mesh position={[0.38, -3.84, 0.16]} rotation={[0.14, 0, -0.08]} scale={[0.34, 0.11, 0.16]} castShadow receiveShadow>
        <capsuleGeometry args={[0.5, 0.8, 8, 16]} />
        {material}
      </mesh>
    </group>
  );
}

function CameraRig({
  focus,
  selectedRegionKey,
  resetSignal,
}: {
  focus: 'full' | 'core' | 'upper' | 'lower';
  selectedRegionKey: BodyRegionKey | null;
  resetSignal: number;
}) {
  const { camera } = useThree();
  const targetRef = useRef(new THREE.Vector3(...CAMERA_PRESETS[focus].target));
  const controlsRef = useRef<any>(null);
  const selectedMesh = selectedRegionKey ? REGION_MESHES[selectedRegionKey] : null;

  useEffect(() => {
    const preset = CAMERA_PRESETS[focus];
    const nextPos = new THREE.Vector3(...preset.position);
    const nextTarget = selectedMesh
      ? new THREE.Vector3(selectedMesh.position[0], selectedMesh.position[1], selectedMesh.position[2])
      : new THREE.Vector3(...preset.target);
    (camera as THREE.PerspectiveCamera).userData.targetPosition = nextPos;
    targetRef.current = nextTarget;
  }, [focus, selectedRegionKey, camera, selectedMesh]);

  useEffect(() => {
    const preset = CAMERA_PRESETS.full;
    const resetPosition = new THREE.Vector3(...preset.position);
    const resetTarget = new THREE.Vector3(...preset.target);
    camera.position.copy(resetPosition);
    targetRef.current = resetTarget;
    (camera as THREE.PerspectiveCamera).userData.targetPosition = resetPosition;
    if (controlsRef.current) {
      controlsRef.current.target.copy(resetTarget);
      controlsRef.current.update();
    }
  }, [camera, resetSignal]);

  useFrame(() => {
    const targetPosition = (camera as THREE.PerspectiveCamera).userData.targetPosition as THREE.Vector3 | undefined;
    if (targetPosition) {
      camera.position.lerp(targetPosition, 0.08);
    }
    if (controlsRef.current) {
      controlsRef.current.target.lerp(targetRef.current, 0.08);
      controlsRef.current.update();
    }
  });

  return (
    <OrbitControls
      ref={controlsRef}
      enablePan={false}
      enableDamping
      dampingFactor={0.12}
      minDistance={2.4}
      maxDistance={8}
      minPolarAngle={0.7}
      maxPolarAngle={2.25}
    />
  );
}

export default function BodyMapCanvas({ selectedRegionKey, onSelectRegion, issues }: Props) {
  const [overlay, setOverlay] = useState<'all' | BodySystemTag>('all');
  const [zoom, setZoom] = useState<'full' | 'core' | 'upper' | 'lower'>('full');
  const [hoveredRegionKey, setHoveredRegionKey] = useState<BodyRegionKey | null>(null);
  const [resetSignal, setResetSignal] = useState(0);

  const regionStats = useMemo(() => buildRegionStats(issues), [issues]);
  const trackedRegionCount = useMemo(() => Object.keys(regionStats).length, [regionStats]);
  const visibleRegions = useMemo(
    () => BODY_REGION_DEFINITIONS.filter((region) => overlay === 'all' || region.systemTags.includes(overlay)),
    [overlay],
  );
  const completionPercent = Math.round((trackedRegionCount / BODY_REGION_DEFINITIONS.length) * 100);
  const activeSystems = useMemo(
    () => Array.from(new Set(issues.flatMap((issue) => issue.systemTags))),
    [issues],
  );
  const selectedRegion = BODY_REGION_DEFINITIONS.find((region) => region.key === selectedRegionKey) || null;
  const hoveredRegion = BODY_REGION_DEFINITIONS.find((region) => region.key === hoveredRegionKey) || null;
  const autoZoom = selectedRegion?.zoomGroup || zoom;
  const selectedIssues = useMemo(
    () => (selectedRegionKey ? issues.filter((issue) => issue.regionKey === selectedRegionKey) : []),
    [issues, selectedRegionKey],
  );
  const hoveredIssues = useMemo(
    () => (hoveredRegionKey ? issues.filter((issue) => issue.regionKey === hoveredRegionKey) : []),
    [issues, hoveredRegionKey],
  );
  const infoRegion = selectedRegion || hoveredRegion;
  const infoIssues = selectedIssues.length > 0 ? selectedIssues : hoveredIssues;

  return (
    <section className="rounded-3xl border p-6 md:p-8 bg-white" style={{ borderColor: 'var(--melio-border)' }}>
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] mb-2" style={{ color: 'var(--melio-sage)' }}>
            Body map
          </p>
          <h2 className="text-2xl font-normal" style={{ fontFamily: "var(--melio-display)", color: 'var(--melio-charcoal)' }}>
            Choose a body region
          </h2>
        </div>
      </div>

      <p className="text-sm mb-6" style={{ color: 'var(--melio-text-muted)' }}>
        Drag to rotate the body continuously, zoom with the trackpad or mousewheel, and use overlays to move between a visual diary and a structured data map.
      </p>

      <div className="rounded-2xl border p-4 mb-5" style={{ borderColor: 'var(--melio-border)', backgroundColor: 'var(--b-bone)' }}>
        <div className="flex flex-wrap items-center justify-between gap-4 mb-3">
          <div className="flex flex-wrap gap-2">
            {(['full', 'upper', 'core', 'lower'] as const).map((zoomLevel) => (
              <button
                key={zoomLevel}
                type="button"
                onClick={() => setZoom(zoomLevel)}
                className="px-3 py-2 rounded-full text-xs uppercase tracking-wide"
                style={{
                  backgroundColor: zoom === zoomLevel ? 'rgba(79, 93, 24, 0.2)' : 'white',
                  color: 'var(--melio-charcoal)',
                  border: '1px solid var(--melio-border)',
                }}
              >
                {zoomLevel}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          {SYSTEM_OVERLAYS.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setOverlay(item.id)}
              className="px-3 py-2 rounded-full text-sm"
              style={{
                backgroundColor: overlay === item.id ? 'rgba(84, 112, 146, 0.18)' : 'white',
                color: 'var(--melio-charcoal)',
                border: '1px solid var(--melio-border)',
              }}
            >
              {item.label}
            </button>
          ))}
        </div>

        <div className="grid xl:grid-cols-[minmax(0,1.45fr)_18rem] gap-4 items-start">
          <div className="rounded-2xl border p-4 bg-white" style={{ borderColor: 'var(--melio-border)' }}>
            <div className="h-[40rem] rounded-2xl overflow-hidden" style={{ background: 'radial-gradient(circle at 50% 26%, rgba(255,255,255,0.98) 0%, rgba(244,240,232,0.95) 58%, rgba(234,229,220,0.92) 100%)' }}>
              <Canvas shadows camera={{ position: CAMERA_PRESETS[autoZoom].position, fov: 32 }}>
                <ambientLight intensity={1.25} />
                <directionalLight position={[4, 6, 5]} intensity={1.1} castShadow />
                <directionalLight position={[-4, 2, -5]} intensity={0.45} />
                <BaseBody />
                {visibleRegions.map((region) => (
                  <RegionMesh
                    key={region.key}
                    regionKey={region.key}
                    selected={selectedRegionKey === region.key}
                    stats={regionStats[region.key]}
                    onSelect={onSelectRegion}
                    onHover={setHoveredRegionKey}
                  />
                ))}
                <CameraRig focus={autoZoom} selectedRegionKey={selectedRegionKey} resetSignal={resetSignal} />
                {infoRegion && (
                  <Html
                    position={(REGION_MESHES[infoRegion.key]?.position || [0, 1.6, 0]) as [number, number, number]}
                    center
                    distanceFactor={8}
                    style={{ pointerEvents: 'none' }}
                  >
                    <div
                      style={{
                        minWidth: 180,
                        maxWidth: 240,
                        padding: '12px 14px',
                        borderRadius: 18,
                        background: 'rgba(255,255,255,0.97)',
                        border: '1px solid rgba(79, 93, 24,0.25)',
                        boxShadow: '0 18px 40px rgba(26, 26, 24,0.16)',
                        color: 'var(--melio-charcoal)',
                      }}
                    >
                      <div style={{ fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--melio-text-muted)', marginBottom: 6 }}>
                        {selectedRegion ? 'Selected region' : 'Hover region'}
                      </div>
                      <div style={{ fontWeight: 600, marginBottom: 4 }}>
                        {infoRegion.label}
                      </div>
                      <div style={{ fontSize: 13, color: 'var(--melio-text-muted)' }}>
                        {infoIssues.length > 0 ? `${infoIssues.length} tracked issue(s)` : 'No issue logged here yet'}
                      </div>
                      {infoIssues[0]?.symptomSummary ? (
                        <div style={{ fontSize: 13, marginTop: 8 }}>
                          {infoIssues[0].symptomSummary}
                        </div>
                      ) : null}
                    </div>
                  </Html>
                )}
              </Canvas>
            </div>

            <div className="flex items-center justify-center gap-3 mt-3">
              <button
                type="button"
                onClick={() => {
                  setZoom('full');
                  setOverlay('all');
                  setHoveredRegionKey(null);
                  onSelectRegion(null);
                  setResetSignal((current) => current + 1);
                }}
                className="px-3 py-2 rounded-full text-sm"
                style={{ backgroundColor: '#EFEEE9', color: 'var(--melio-charcoal)', border: '1px solid var(--melio-border)' }}
              >
                Reset view
              </button>
              <div className="text-xs uppercase tracking-[0.2em]" style={{ color: 'var(--melio-text-muted)' }}>
                Drag to orbit
              </div>
              <button
                type="button"
                onClick={() => {
                  setOverlay('all');
                  setHoveredRegionKey(null);
                  onSelectRegion(null);
                }}
                className="px-3 py-2 rounded-full text-sm"
                style={{ backgroundColor: '#EFEEE9', color: 'var(--melio-charcoal)', border: '1px solid var(--melio-border)' }}
              >
                Clear filters
              </button>
            </div>
          </div>

          <div className="space-y-4">
            <div className="rounded-2xl border p-4 bg-white" style={{ borderColor: 'var(--melio-border)' }}>
              <p className="text-xs uppercase tracking-wide mb-2" style={{ color: 'var(--melio-text-muted)' }}>
                Completion
              </p>
              <div className="text-3xl font-medium mb-2" style={{ color: 'var(--melio-charcoal)' }}>
                {completionPercent}%
              </div>
              <div className="h-2 rounded-full overflow-hidden mb-2" style={{ backgroundColor: 'rgba(26, 26, 24,0.08)' }}>
                <div style={{ width: `${completionPercent}%`, height: '100%', backgroundColor: 'var(--melio-sage)' }} />
              </div>
              <p className="text-sm" style={{ color: 'var(--melio-text-muted)' }}>
                {trackedRegionCount} / {BODY_REGION_DEFINITIONS.length} regions explored
              </p>
            </div>

            <div className="rounded-2xl border p-4 bg-white" style={{ borderColor: 'var(--melio-border)' }}>
              <p className="text-xs uppercase tracking-wide mb-2" style={{ color: 'var(--melio-text-muted)' }}>
                Heat legend
              </p>
              <div className="space-y-2 text-sm" style={{ color: 'var(--melio-charcoal)' }}>
                <div className="flex items-center gap-2"><span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: 'rgba(26, 26, 24, 0.08)' }} /> Unexplored</div>
                <div className="flex items-center gap-2"><span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: 'rgba(79, 93, 24, 0.45)' }} /> Historical / resolved</div>
                <div className="flex items-center gap-2"><span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: 'rgba(196, 164, 132, 0.62)' }} /> Active</div>
                <div className="flex items-center gap-2"><span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: 'rgba(199, 123, 99, 0.72)' }} /> Recurrent / clustered</div>
                <div className="flex items-center gap-2"><span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: 'rgba(185, 28, 28, 0.75)' }} /> Escalated</div>
              </div>
            </div>

            <div className="rounded-2xl border p-4 bg-white" style={{ borderColor: 'var(--melio-border)' }}>
              <p className="text-xs uppercase tracking-wide mb-2" style={{ color: 'var(--melio-text-muted)' }}>
                Rotation
              </p>
              <div className="text-sm leading-relaxed" style={{ color: 'var(--melio-text-muted)' }}>
                This is a true 3D scene with continuous orbit controls. Drag to rotate, use the mouse wheel or trackpad to zoom, and click regions directly on the body.
              </div>
            </div>

            <div className="rounded-2xl border p-4 bg-white" style={{ borderColor: 'var(--melio-border)' }}>
              <p className="text-xs uppercase tracking-wide mb-2" style={{ color: 'var(--melio-text-muted)' }}>
                Systems tracked
              </p>
              <div className="flex flex-wrap gap-2">
                {activeSystems.length === 0 ? (
                  <span className="text-sm" style={{ color: 'var(--melio-text-muted)' }}>No systems mapped yet</span>
                ) : activeSystems.map((system) => (
                  <span
                    key={system}
                    className="px-2.5 py-1 rounded-full text-xs"
                    style={{ backgroundColor: 'rgba(84, 112, 146, 0.12)', color: 'var(--melio-charcoal)' }}
                  >
                    {system}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-5">
        <div>
          <p className="text-xs uppercase tracking-wide mb-3" style={{ color: 'var(--melio-text-muted)' }}>
            Zoom view regions
          </p>
          <div className="flex flex-wrap gap-2">
            {visibleRegions.map((region) => {
              const stats = regionStats[region.key];
              const selected = selectedRegionKey === region.key;
              return (
                <button
                  key={region.key}
                  type="button"
                  onClick={() => onSelectRegion(region.key)}
                  className="px-3 py-2 rounded-full text-sm transition-opacity hover:opacity-90"
                  style={{
                    backgroundColor: selected ? 'var(--melio-charcoal)' : stats ? 'rgba(79, 93, 24, 0.12)' : '#EFEEE9',
                    color: selected ? 'var(--melio-cream)' : 'var(--melio-charcoal)',
                  }}
                >
                  {region.label}{stats?.active ? ` (${stats.active})` : ''}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
